import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

type Format = 'foreign_en' | 'local_zh'

type RewriteResult = {
  resume: string
  sessionId: string
  remainingRewrites: number
}

// In dev, vite proxies /api → localhost:8787. In production set VITE_WORKER_URL.
const WORKER_BASE = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? ''

function getSessionId(): string {
  const key = 'rw_session_id'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem(key, id)
  }
  return id
}

export function AppPage() {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [resume, setResume] = useState('')
  const [jobDescription, setJobDescription] = useState('')
  const [format, setFormat] = useState<Format>('local_zh')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<RewriteResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh-TW' ? 'en' : 'zh-TW')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setResult(null)
    setLoading(true)

    const sessionId = getSessionId()

    try {
      const res = await fetch(`${WORKER_BASE}/api/resume/rewrite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify({ resume, jobDescription, format }),
      })

      const data = (await res.json()) as RewriteResult | { error: string; code: number }

      if (!res.ok) {
        const errData = data as { error: string; code: number }
        setError(errData.error ?? t('app.error.generic'))
        return
      }

      const successData = data as RewriteResult
      if (successData.sessionId) {
        sessionStorage.setItem('rw_session_id', successData.sessionId)
      }
      setResult(successData)

      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch {
      setError(t('app.error.network'))
    } finally {
      setLoading(false)
    }
  }

  function handlePrint() {
    window.print()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4 print:hidden">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-indigo-600">
            履歷魔法師
          </Link>
          <div className="flex items-center gap-4">
            {user?.plan === 'pro' && (
              <span className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                Pro
              </span>
            )}
            {user && (
              <span className="text-sm text-gray-500 hidden sm:block">{user.email}</span>
            )}
            <button onClick={toggleLang} className="text-sm text-gray-500 hover:text-gray-900">
              {i18n.language === 'zh-TW' ? 'EN' : '中文'}
            </button>
            <button
              onClick={() => { logout(); navigate('/login') }}
              className="text-sm text-gray-500 hover:text-gray-900"
            >
              登出
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-10 print:hidden">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('app.title')}</h1>
          <p className="text-gray-600 mt-2">{t('app.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Format Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('app.format.label')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="local_zh"
                  checked={format === 'local_zh'}
                  onChange={() => setFormat('local_zh')}
                  className="accent-indigo-600"
                />
                <span className="text-sm font-medium text-gray-900">
                  {t('app.format.localZh')}
                </span>
                <span className="text-xs text-gray-500">{t('app.format.localZhDesc')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="foreign_en"
                  checked={format === 'foreign_en'}
                  onChange={() => setFormat('foreign_en')}
                  className="accent-indigo-600"
                />
                <span className="text-sm font-medium text-gray-900">
                  {t('app.format.foreignEn')}
                </span>
                <span className="text-xs text-gray-500">{t('app.format.foreignEnDesc')}</span>
              </label>
            </div>
          </div>

          {/* Job Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="jd">
              {t('app.jd.label')}
            </label>
            <textarea
              id="jd"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder={t('app.jd.placeholder')}
              required
              rows={6}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
            />
          </div>

          {/* Resume Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="resume">
              {t('app.resume.label')}
            </label>
            <textarea
              id="resume"
              value={resume}
              onChange={(e) => setResume(e.target.value)}
              placeholder={t('app.resume.placeholder')}
              required
              rows={12}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y font-mono"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !resume.trim() || !jobDescription.trim()}
            className="w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-semibold text-base hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('app.submit.loading') : t('app.submit.label')}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div ref={resultRef} className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t('app.result.title')}</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {t('app.result.remaining', { count: result.remainingRewrites })}
                </span>
                <button
                  onClick={handlePrint}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  {t('app.result.download')}
                </button>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                {result.resume}
              </pre>
            </div>
            {result.remainingRewrites === 0 && user?.plan !== 'pro' && (
              <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl px-6 py-4">
                <p className="text-sm font-semibold text-indigo-900 mb-1">
                  本月免費次數已用完
                </p>
                <p className="text-sm text-indigo-700 mb-3">
                  升級 Pro 方案，享無限次 AI 改寫 + 優先服務，每月只需 NT$199。
                </p>
                <Link
                  to="/#pricing"
                  className="inline-block bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  立即升級 Pro →
                </Link>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Print-only resume view */}
      {result && (
        <div className="hidden print:block p-8">
          <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-black">
            {result.resume}
          </pre>
        </div>
      )}
    </div>
  )
}
